import onetime from "@-/com.npmjs.onetime";

/**
 * Undo can reach back through a whole study session, so a record has to outlive many later
 * completions. Each one is a few dozen bytes; the bound only stops a very long session from growing
 * the map without limit.
 */
const SLIDE_COMPLETION_PRIOR_LIMIT = 1024;

const getSlideCompletionPriors = onetime(() => new Map<string, string | null>());

/**
 * Records the `completedAt` that completion `completionId` found before it wrote (`null` = the
 * slide was not complete). The first record wins: a retry of the same completion runs after the
 * first attempt's write and would otherwise record that write as the earlier state.
 */
export function recordSlideCompletionPrior(
  completionId: string,
  completedAtBefore: string | null,
): void {
  const priors = getSlideCompletionPriors();
  if (priors.has(completionId)) {
    return;
  }
  priors.set(completionId, completedAtBefore);
  const [oldest] = priors.keys();
  if (oldest !== undefined && priors.size > SLIDE_COMPLETION_PRIOR_LIMIT) {
    priors.delete(oldest);
  }
}

/**
 * Removes and returns completion `completionId`'s record. Removing it means a later completion
 * under the same id (Redo after Undo) records the state as it is then. `undefined` when the
 * completion failed before it read the row, or its record aged out.
 */
export function takeSlideCompletionPrior(
  completionId: string,
): { completedAtBefore: string | null } | undefined {
  const priors = getSlideCompletionPriors();
  if (!priors.has(completionId)) {
    return undefined;
  }
  const completedAtBefore = priors.get(completionId) ?? null;
  priors.delete(completionId);
  return { completedAtBefore };
}
